import { useEffect, useState } from "react";
import { Button, Card, Container, Navbar } from "react-bootstrap";
import { FaCopy, FaExclamationTriangle } from "react-icons/fa";
import { isRouteErrorResponse, Link, useRouteError } from "react-router";

import { useUser } from "../../contexts/user_provider";
import urls from "../../urls";

const SENSITIVE_KEY_PATTERN =
    /token|authorization|password|secret|credential|cookie|claim/i;
const MAX_DIAGNOSTIC_VALUE_LENGTH = 4000;

function truncateDiagnosticValue(value) {
    if (value.length <= MAX_DIAGNOSTIC_VALUE_LENGTH) {
        return value;
    }

    return `${value.slice(0, MAX_DIAGNOSTIC_VALUE_LENGTH)}… (truncated)`;
}

function formatSimpleDiagnosticValue(value) {
    if (typeof value === "string") {
        return truncateDiagnosticValue(value);
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    return `[${value === null ? "null" : typeof value}]`;
}

function getResponseData(errorData) {
    if (
        typeof errorData === "string" ||
        typeof errorData === "number" ||
        typeof errorData === "boolean"
    ) {
        return formatSimpleDiagnosticValue(errorData);
    }

    return undefined;
}

function getStringProperty(value, property) {
    try {
        const propertyValue = value?.[property];
        return typeof propertyValue === "string" ? propertyValue : undefined;
    } catch {
        return undefined;
    }
}

function getCurrentPath() {
    try {
        const { pathname, search } = window.location;
        const searchParams = new URLSearchParams(search);

        searchParams.forEach((_, key) => {
            if (SENSITIVE_KEY_PATTERN.test(key)) {
                searchParams.set(key, "[redacted]");
            }
        });

        const sanitizedSearch = searchParams.toString();
        return `${pathname}${sanitizedSearch ? `?${sanitizedSearch}` : ""}`;
    } catch {
        return "[Unavailable]";
    }
}

function getErrorDetails(error) {
    try {
        if (isRouteErrorResponse(error)) {
            return {
                type: "React Router error response",
                message:
                    error.statusText ||
                    `Request failed with status ${error.status}`,
                status: String(error.status),
                statusText: error.statusText || "[Not provided]",
                responseData: getResponseData(error.data),
            };
        }
    } catch {
        // Fall through to the defensive handling for unusual thrown values.
    }

    const name = getStringProperty(error, "name");
    const message = getStringProperty(error, "message");
    const stack = getStringProperty(error, "stack");

    return {
        type: name || typeof error,
        message: message || formatSimpleDiagnosticValue(error),
        stack,
    };
}

function getIsAdmin(userContext) {
    try {
        return Boolean(userContext?.getUserIsAdmin?.());
    } catch {
        return false;
    }
}

function getStackFrames(stack) {
    return stack?.split(/\r?\n/).filter((frame) => frame.trim()) || [];
}

function getDiagnosticReport({
    errorDetails,
    currentPath,
    timestamp,
    environment,
    userAgent,
}) {
    const lines = [
        `Error type: ${errorDetails.type}`,
        `Error message: ${errorDetails.message}`,
    ];

    if (errorDetails.stack) {
        lines.push("Stack trace:", errorDetails.stack);
    }

    if (errorDetails.status) {
        lines.push(
            `Response status: ${errorDetails.status}`,
            `Response status text: ${errorDetails.statusText}`,
        );

        if (errorDetails.responseData !== undefined) {
            lines.push(`Response data: ${errorDetails.responseData}`);
        }
    }

    lines.push(
        `Current path: ${currentPath}`,
        `Timestamp: ${timestamp}`,
        `Environment: ${environment}`,
        `Browser: ${userAgent}`,
    );

    return lines.join("\n");
}

export default function ErrorPage() {
    const error = useRouteError();
    const userContext = useUser();
    const [copyStatus, setCopyStatus] = useState("idle");
    const [timestamp] = useState(() => new Date().toISOString());

    useEffect(() => {
        if (copyStatus === "idle") {
            return undefined;
        }

        const timeout = setTimeout(() => setCopyStatus("idle"), 2500);
        return () => clearTimeout(timeout);
    }, [copyStatus]);

    const isAdmin = getIsAdmin(userContext);
    const errorDetails = getErrorDetails(error);
    const stackFrames = getStackFrames(errorDetails.stack);
    const currentPath = getCurrentPath();
    const environment = import.meta.env.MODE || "[Unavailable]";
    const userAgent =
        typeof navigator === "undefined"
            ? "[Unavailable]"
            : navigator.userAgent;
    const diagnosticReport = getDiagnosticReport({
        errorDetails,
        currentPath,
        timestamp,
        environment,
        userAgent,
    });

    async function handleCopyDiagnostics() {
        try {
            if (
                typeof navigator === "undefined" ||
                !navigator.clipboard?.writeText
            ) {
                throw new Error("Clipboard is unavailable");
            }

            await navigator.clipboard.writeText(diagnosticReport);
            setCopyStatus("success");
        } catch {
            setCopyStatus("error");
        }
    }

    return (
        <>
            <title>Something went wrong | MyCorvus</title>
            <Navbar
                variant="light"
                className="py-3"
                style={{
                    backgroundColor: "#fff",
                    borderBottom: "1px solid rgba(0,0,0,.125)",
                }}
            >
                <Container style={{ maxWidth: "850px" }}>
                    <Navbar.Brand
                        as={Link}
                        to={urls.home()}
                        className="d-flex align-items-center"
                    >
                        <img
                            src="/MyCorvus.png"
                            width="auto"
                            height="20"
                            className="d-inline-block align-center me-2"
                            alt="MyCorvus"
                        />
                    </Navbar.Brand>
                </Container>
            </Navbar>

            <Container style={{ maxWidth: "850px" }} className="my-4 my-md-5">
                <Card>
                    <Card.Body className="p-4 text-center">
                        <h1 className="h3 mb-3">
                            <FaExclamationTriangle
                                style={{ marginTop: "-2px" }}
                                className="me-2"
                            />
                            Something went wrong
                        </h1>
                        <p className="mb-4">
                            We could not load this page. Please try again, or
                            return to home.
                        </p>
                        <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                            <Button onClick={() => window.location.reload()}>
                                Try again
                            </Button>
                            <Button
                                as={Link}
                                to={urls.home()}
                                variant="outline-secondary"
                            >
                                Go to home
                            </Button>
                        </div>
                    </Card.Body>
                </Card>

                {isAdmin && (
                    <Card className="mt-3">
                        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                            <Card.Title as="h2" className="h5 mb-0 me-sm-auto">
                                Technical details
                            </Card.Title>
                            <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                                {copyStatus === "success" && (
                                    <span
                                        className="small text-success"
                                        role="status"
                                    >
                                        Copied!
                                    </span>
                                )}
                                {copyStatus === "error" && (
                                    <span
                                        className="small text-danger"
                                        role="status"
                                    >
                                        Failed to copy!
                                    </span>
                                )}
                                <Button
                                    variant="outline-secondary"
                                    onClick={handleCopyDiagnostics}
                                >
                                    <FaCopy className="me-2" />
                                    Copy
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <dl className="mb-0 text-start">
                                <dt>Error type</dt>
                                <dd>{errorDetails.type}</dd>

                                <dt>Message</dt>
                                <dd className="text-break">
                                    {errorDetails.message}
                                </dd>

                                {stackFrames.length > 0 && (
                                    <>
                                        <dt>Stack trace</dt>
                                        <dd>
                                            <div className="list-group mb-3">
                                                {stackFrames.map(
                                                    (frame, index) => (
                                                        <div
                                                            // biome-ignore lint/suspicious/noArrayIndexKey: Stack frames are positional, may contain duplicates, and have no stable identifier.
                                                            key={`${index}-${frame}`}
                                                            className="list-group-item bg-light font-monospace small"
                                                            style={{
                                                                whiteSpace:
                                                                    "pre-wrap",
                                                                overflowWrap:
                                                                    "anywhere",
                                                            }}
                                                        >
                                                            {frame}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        </dd>
                                    </>
                                )}

                                {errorDetails.status && (
                                    <>
                                        <dt>Response status</dt>
                                        <dd>{errorDetails.status}</dd>

                                        <dt>Response status text</dt>
                                        <dd>{errorDetails.statusText}</dd>

                                        {errorDetails.responseData !==
                                            undefined && (
                                            <>
                                                <dt>Response data</dt>
                                                <dd className="text-break">
                                                    {errorDetails.responseData}
                                                </dd>
                                            </>
                                        )}
                                    </>
                                )}

                                <dt>Current path</dt>
                                <dd className="text-break">{currentPath}</dd>

                                <dt>Timestamp</dt>
                                <dd>{timestamp}</dd>

                                <dt>Environment</dt>
                                <dd>{environment}</dd>

                                <dt className="mb-0">Browser</dt>
                                <dd className="mb-0 text-break">{userAgent}</dd>
                            </dl>
                        </Card.Body>
                    </Card>
                )}
            </Container>
        </>
    );
}
